/**
 * Number Inventory Table
 *
 * Renders the inventory grid with multi-select, assignment chips, and per-row
 * actions. Numbers absent from the local DB show as "Unimported" — clicking
 * Assign on those triggers an import-then-assign on the server side.
 */
import { useState } from 'react';
import {
  Phone,
  Star,
  StarOff,
  X,
  Loader2,
  Plus,
  Users,
  AlertTriangle,
  Check,
  MessageSquare,
  Image as ImageIcon,
  Mic,
} from 'lucide-react';
import { cn, Button } from '@respectly/ui';
import type { InventoryItem } from '../../stores/useNumberInventoryStore';

interface Props {
  items: InventoryItem[];
  isLoading: boolean;
  selectedIds: Set<string>;
  onToggleSelect: (telnyxId: string) => void;
  onToggleSelectAll: () => void;
  onAssign: (item: InventoryItem) => void;
  onUnassign: (phoneNumberId: string, tenantId: string) => Promise<boolean> | boolean;
  onSetDefault: (
    phoneNumberId: string,
    tenantId: string,
    isDefault: boolean,
  ) => Promise<boolean> | boolean;
  isUnassigning: boolean;
}

function formatE164(num: string): string {
  const m = num.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return num;
}

function statusOf(item: InventoryItem): 'unassigned' | 'dedicated' | 'shared' {
  if (item.assignments.length === 0) return 'unassigned';
  if (item.assignments.length === 1) return 'dedicated';
  return 'shared';
}

export function NumberInventoryTable({
  items,
  isLoading,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onAssign,
  onUnassign,
  onSetDefault,
  isUnassigning,
}: Props) {
  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0 && !allSelected;
  const [busyKey, setBusyKey] = useState<string | null>(null);

  if (isLoading && items.length === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center dark:border-slate-800 dark:bg-slate-900/40">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-400" />
        <p className="mt-2 text-sm text-slate-500">Loading numbers…</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-10 text-center dark:border-slate-700 dark:bg-slate-900/20">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <Phone className="h-5 w-5 text-slate-400" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">
          No numbers match your filters
        </h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
          Buy numbers on the Telnyx portal first, then they&apos;ll appear here for assignment.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900/40">
      {/* Header row */}
      <div className="grid grid-cols-[28px_2fr_1fr_auto_2fr_auto] gap-3 border-b border-slate-100 bg-slate-50/60 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:border-slate-800 dark:bg-slate-950/40">
        <span>
          <input
            type="checkbox"
            aria-label="Select all"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={onToggleSelectAll}
            className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-700"
          />
        </span>
        <span>Number</span>
        <span>Type · Capabilities</span>
        <span>Status</span>
        <span>Assignments</span>
        <span className="text-right">Actions</span>
      </div>

      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {items.map((item) => {
          const status = statusOf(item);
          return (
            <li
              key={item.telnyx.id}
              className="grid grid-cols-[28px_2fr_1fr_auto_2fr_auto] items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50/60 dark:hover:bg-slate-900/40"
            >
              {/* Select */}
              <div>
                <input
                  type="checkbox"
                  aria-label={`Select ${item.telnyx.phoneNumber}`}
                  checked={selectedIds.has(item.telnyx.id)}
                  onChange={() => onToggleSelect(item.telnyx.id)}
                  className="h-3.5 w-3.5 rounded border-slate-300 dark:border-slate-700"
                />
              </div>

              {/* Number */}
              <div className="min-w-0">
                <div className="font-mono text-[14px] tabular-nums font-semibold text-slate-900 dark:text-white">
                  {formatE164(item.telnyx.phoneNumber)}
                </div>
                {item.local?.friendlyName && (
                  <div className="truncate text-xs text-slate-500">{item.local.friendlyName}</div>
                )}
                {!item.inDb && (
                  <div className="mt-0.5 inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                    Unimported
                  </div>
                )}
              </div>

              {/* Type · Capabilities */}
              <div className="flex flex-col gap-1.5">
                <span className="text-[11px] uppercase tracking-wide text-slate-400">
                  {item.telnyx.type === 'tollfree' ? 'Toll-free' : item.telnyx.type}
                </span>
                <div className="flex items-center gap-1">
                  <CapPill on={item.telnyx.capabilities.sms} icon={MessageSquare} label="SMS" />
                  <CapPill on={item.telnyx.capabilities.mms} icon={ImageIcon} label="MMS" />
                  <CapPill on={item.telnyx.capabilities.voice} icon={Mic} label="Voice" />
                </div>
              </div>

              {/* Status */}
              <StatusBadge status={status} count={item.assignments.length} />

              {/* Assignments (chips) */}
              <div className="flex flex-wrap items-center gap-1.5">
                {item.assignments.length === 0 ? (
                  <span className="text-xs italic text-slate-400">No tenants assigned</span>
                ) : (
                  item.assignments.map((a) => {
                    const key = `${item.local?.id ?? item.telnyx.id}:${a.tenantId}`;
                    const busy = busyKey === key;
                    return (
                      <span
                        key={a.tenantId}
                        className="group inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                        title={a.isDefault ? 'Default sender for this tenant' : undefined}
                      >
                        {a.isDefault && <Star className="h-3 w-3 fill-amber-500 text-amber-500" />}
                        <span className="max-w-[14ch] truncate">{a.tenantName}</span>
                        {item.local && !a.isDefault && (
                          <button
                            type="button"
                            title="Set as default sender for this tenant"
                            onClick={async () => {
                              if (!item.local) return;
                              setBusyKey(key);
                              await onSetDefault(item.local.id, a.tenantId, true);
                              setBusyKey(null);
                            }}
                            disabled={busy}
                            className="ml-0.5 text-slate-300 hover:text-amber-500 disabled:opacity-50"
                          >
                            <StarOff className="h-3 w-3" />
                          </button>
                        )}
                        <button
                          type="button"
                          title="Remove this assignment"
                          onClick={async () => {
                            if (!item.local) return;
                            setBusyKey(key);
                            await onUnassign(item.local.id, a.tenantId);
                            setBusyKey(null);
                          }}
                          disabled={busy || isUnassigning || !item.local}
                          className="ml-0.5 text-slate-300 hover:text-rose-500 disabled:opacity-50"
                        >
                          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                        </button>
                      </span>
                    );
                  })
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onAssign(item)}
                  className="border-slate-300 dark:border-slate-700"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Assign
                </Button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatusBadge({
  status,
  count,
}: {
  status: 'unassigned' | 'dedicated' | 'shared';
  count: number;
}) {
  if (status === 'unassigned') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700 ring-1 ring-amber-200/70 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-800/50">
        <AlertTriangle className="h-3 w-3" />
        Unassigned
      </span>
    );
  }
  if (status === 'dedicated') {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-sky-700 ring-1 ring-sky-200/60 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50">
        <Check className="h-3 w-3" />
        Dedicated
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-700 ring-1 ring-violet-200/60 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-800/50">
      <Users className="h-3 w-3" />
      Shared · {count}
    </span>
  );
}

function CapPill({
  on,
  icon: Icon,
  label,
}: {
  on: boolean;
  icon: typeof MessageSquare;
  label: string;
}) {
  return (
    <span
      title={`${label}: ${on ? 'enabled' : 'disabled'}`}
      className={cn(
        'inline-flex h-5 items-center gap-1 rounded-md px-1.5 text-[10px] font-bold uppercase tracking-wide ring-1',
        on
          ? 'bg-sky-50 text-sky-700 ring-sky-200/60 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50'
          : 'bg-slate-50 text-slate-300 ring-slate-200 dark:bg-slate-900/40 dark:text-slate-600 dark:ring-slate-800',
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
